import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, CaretDown, Sparkle, CurrencyCircleDollar, Tag, ArrowsClockwise, ChartBar, DownloadSimple, CalendarBlank } from '@phosphor-icons/react';
import Logo from '../components/Logo';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-medium text-slate-800 text-sm sm:text-base pr-4">{question}</span>
        <CaretDown size={20} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="text-sm text-slate-500 leading-relaxed pb-5 -mt-1">{answer}</p>
      )}
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1 sm:gap-3">
            <a href="#planos" className="text-sm font-medium text-slate-500 hover:text-slate-800 transition px-2 sm:px-3 py-2 hidden sm:block">
              Planos
            </a>
            <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-800 transition px-3 py-2">
              Entrar
            </Link>
            <Link to="/register" className="text-sm font-medium bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
              Criar conta
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 relative">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6">
            <Sparkle size={14} />
            Gratis para comecar
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
            Suas financas organizadas de forma <span className="text-blue-600">simples</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto">
            O DespEasy te ajuda a entender para onde seu dinheiro vai. Registre despesas, acompanhe por categoria, exporte relatorios e tenha clareza sobre seus gastos.
          </p>
          <div className="flex items-center justify-center gap-4 mb-10">
            <Link
              to="/register"
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition font-medium shadow-sm text-sm"
            >
              Comecar gratis
            </Link>
            <a
              href="#como-funciona"
              className="text-slate-600 px-6 py-3 rounded-xl hover:bg-slate-50 transition font-medium text-sm border border-slate-200"
            >
              Como funciona
            </a>
          </div>

          {/* Badges */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {['Sem cartao de credito', '10 despesas gratis por mes', 'Exportar CSV'].map((text) => (
              <div key={text} className="flex items-center gap-1.5 bg-slate-100 text-slate-500 text-xs font-medium px-3 py-1.5 rounded-full">
                <Check size={14} className="text-emerald-500" />
                {text}
              </div>
            ))}
          </div>
        </div>

        <a href="#como-funciona" className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <CaretDown size={20} className="text-slate-300" />
        </a>
      </section>

      {/* Como funciona */}
      <section id="como-funciona" className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Como funciona</h2>
            <p className="text-slate-500">Tres passos simples para organizar suas financas</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Crie sua conta', desc: 'Cadastro rapido e gratuito. Com e-mail ou Google, sem cartao de credito.' },
              { step: '2', title: 'Registre suas despesas', desc: 'Adicione gastos com descricao, valor, categoria e data. Configure recorrencias e parcelas.' },
              { step: '3', title: 'Acompanhe e exporte', desc: 'Veja totais por mes, compare periodos, exporte CSV ou PDF e compartilhe relatorios.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 font-bold">{step}</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Tudo que voce precisa</h2>
            <p className="text-slate-500">Funcionalidades pensadas para simplificar sua vida financeira</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Controle mensal', desc: 'Veja quanto esta gastando, compare com o mes anterior e identifique tendencias.', color: 'bg-blue-50', iconColor: 'text-blue-600', icon: CurrencyCircleDollar },
              { title: 'Categorias', desc: 'Organize por categorias padrao ou crie as suas com cores personalizadas.', color: 'bg-amber-50', iconColor: 'text-amber-600', icon: Tag },
              { title: 'Recorrencias', desc: 'Configure despesas que se repetem: diaria, semanal, mensal ou anual.', color: 'bg-emerald-50', iconColor: 'text-emerald-600', icon: ArrowsClockwise },
              { title: 'Relatorios', desc: 'Relatorio mensal detalhado com filtros, busca e ordenacao por coluna.', color: 'bg-purple-50', iconColor: 'text-purple-600', icon: ChartBar },
              { title: 'Exportar PDF e CSV', desc: 'Baixe seus dados em PDF estilizado ou CSV para usar em planilhas.', color: 'bg-rose-50', iconColor: 'text-rose-500', icon: DownloadSimple },
              { title: 'Resumo anual', desc: 'Visao completa do ano com totais mensais e categorias mais gastas.', color: 'bg-cyan-50', iconColor: 'text-cyan-600', icon: CalendarBlank },
            ].map(({ title, desc, color, iconColor, icon: Icon }) => (
              <div key={title} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-slate-200 transition">
                <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-4`}>
                  <Icon size={20} className={iconColor} />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Planos acessiveis</h2>
            <p className="text-slate-500">Comece gratis e faca upgrade quando quiser</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Free */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-800">Gratuito</h3>
              <p className="text-3xl font-bold text-slate-900 mt-3">R$ 0<span className="text-sm font-normal text-slate-400">/mes</span></p>
              <p className="text-xs text-slate-400 mt-1">Para sempre</p>
              <ul className="mt-6 space-y-3">
                {[
                  'Ate 10 despesas por mes',
                  'Ate 1 categoria personalizada',
                  'Ate 2 despesas recorrentes',
                  'Exportar CSV',
                  'Dashboard completo',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check size={16} className="text-emerald-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="w-full mt-6 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition block text-center"
              >
                Comecar gratis
              </Link>
            </div>

            {/* Pro */}
            <div className="bg-white rounded-2xl p-6 border-2 border-blue-500 shadow-sm relative">
              <div className="absolute -top-3 left-4">
                <span className="text-[11px] font-semibold text-white bg-blue-500 px-2.5 py-0.5 rounded-full">Popular</span>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mt-1">Pro</h3>
              <p className="text-3xl font-bold text-slate-900 mt-3">R$ 4<span className="text-lg">,90</span><span className="text-sm font-normal text-slate-400">/mes</span></p>
              <ul className="mt-6 space-y-3">
                {[
                  'Despesas ilimitadas',
                  'Categorias ilimitadas',
                  'Recorrencias ilimitadas',
                  'Despesas parceladas',
                  'Notas nas despesas',
                  'Exportar PDF',
                  'Compartilhar relatorio',
                  'Resumo anual',
                  'E-mail de relatorio mensal',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check size={16} className="text-emerald-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="w-full mt-6 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition block text-center"
              >
                Comecar com Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Perguntas frequentes</h2>
            <p className="text-slate-500">Tire suas duvidas sobre o DespEasy</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 px-6">
            <FAQItem
              question="O DespEasy e gratuito?"
              answer="Sim! O plano gratuito inclui ate 10 despesas por mes, 1 categoria personalizada, 2 recorrencias e exportacao CSV. Voce pode usar para sempre sem pagar nada."
            />
            <FAQItem
              question="Preciso colocar cartao de credito para comecar?"
              answer="Nao. Voce cria sua conta com e-mail ou Google e ja pode comecar a usar imediatamente. So precisa de cartao se decidir assinar o plano Pro."
            />
            <FAQItem
              question="O que ganho com o plano Pro?"
              answer="Despesas, categorias e recorrencias ilimitadas, parcelas, notas, exportacao PDF, compartilhamento de relatorios, resumo anual e e-mail mensal com resumo dos seus gastos."
            />
            <FAQItem
              question="Posso cancelar o Pro a qualquer momento?"
              answer="Sim, sem burocracia. Voce cancela direto pelo site e continua com acesso Pro ate o fim do periodo pago. Depois volta automaticamente para o plano gratuito."
            />
            <FAQItem
              question="Meus dados ficam seguros?"
              answer="Sim. Usamos criptografia, autenticacao JWT e todas as boas praticas de seguranca. Seus dados financeiros nunca sao compartilhados com terceiros."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Pronto para organizar suas financas?</h2>
          <p className="text-slate-400 mb-8">Comece gratis agora. Sem cartao, sem compromisso, sem complicacao.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/register"
              className="bg-blue-600 text-white px-8 py-3.5 rounded-xl hover:bg-blue-700 transition font-medium shadow-sm"
            >
              Criar conta gratuita
            </Link>
            <a
              href="#planos"
              className="text-slate-300 px-8 py-3.5 rounded-xl hover:bg-slate-800 transition font-medium border border-slate-700"
            >
              Ver planos
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <Logo size="sm" />
          <p className="text-xs text-slate-400">DespEasy - Feito para simplificar suas financas.</p>
        </div>
      </footer>
    </div>
  );
}
